import React, { useEffect, useState } from "react";
import {
  useCashbookStore,
  useIncomeStatementStore,
} from "../../../config/stores";
import { Card, Space, Alert, Spin, Form, Typography, message } from "antd";
import IncomeStatementTable from "./IncomeStatementTable";
import { find, isEmpty, map } from "lodash";
import AppButton from "../../../components/common/AppButton";
import AppInput from "../../../components/common/AppInput";
import {
  AiOutlineFileExcel,
  AiOutlineReload,
  AiOutlineFilePdf,
} from "react-icons/ai";
import dayjs from "dayjs";
import generateIncomeStatementPdf from "../../../config/services/incomeStatementPdf";

const { Title } = Typography;

const ProjectIncomeStatement = () => {
  const cashbookStore = useCashbookStore();
  const incomeStatementStore = useIncomeStatementStore();
  const [incomeStatement, setIncomeStatement] = useState(null);
  const [hasLoaded, setHasLoaded] = useState(false);
  const [form] = Form.useForm();
  const [startDate, setStartDate] = useState(dayjs().startOf("month"));
  const [endDate, setEndDate] = useState(dayjs());
  const [projectId, setProjectId] = useState(null);

  const getDefaultContext = () => {
    const startDateValue = startDate
      ? dayjs(startDate).format("YYYY-MM-DD")
      : dayjs().startOf("month").format("YYYY-MM-DD");
    const endDateValue = endDate
      ? dayjs(endDate).format("YYYY-MM-DD")
      : dayjs().format("YYYY-MM-DD");
    return { startDate: startDateValue, endDate: endDateValue, projectId };
  };

  useEffect(() => {
    const defaultContext = getDefaultContext();
    form.setFieldsValue({
      startDate: dayjs(defaultContext.startDate),
      endDate: dayjs(defaultContext.endDate),
      projectId: projectId || undefined,
    });
  }, []);

  useEffect(() => {
    const context = getDefaultContext();
    if (!context.projectId) return;

    const existingData = find(
      incomeStatementStore.incomeStatement,
      (records) =>
        records?.startDate === context.startDate &&
        records?.endDate === context.endDate &&
        records?.projectId === context.projectId
    );

    if (existingData) {
      const data = existingData?.data;
      const incomeStatementData = data?.data || data;
      setIncomeStatement(incomeStatementData);
      setHasLoaded(true);
    }
  }, [incomeStatementStore.incomeStatement, startDate, endDate, projectId]);

  const handleGetIncomeStatement = () => {
    const context = getDefaultContext();
    if (!context.projectId) {
      message.error("Please select a project");
      return;
    }
    if (dayjs(context.endDate).isBefore(dayjs(context.startDate))) {
      message.error("End Date cannot be before Start Date");
      return;
    }
    setHasLoaded(false);
    incomeStatementStore.getIncomeStatement(context).finally(() => {
      setHasLoaded(true);
    });
  };

  const reloadIncomeStatement = () => {
    handleGetIncomeStatement();
  };

  const downloadIncomeStatement = () => {
    const context = getDefaultContext();
    if (!context.projectId) return;
    const fileName = `PROJECT_INCOME_STATEMENT_${context.projectId}_${context.startDate}_TO_${context.endDate}.xlsx`;
    incomeStatementStore.downloadIncomeStatement(context, fileName);
  };

  const downloadIncomeStatementPdf = () => {
    if (incomeStatement) {
      const context = getDefaultContext();
      generateIncomeStatementPdf(
        incomeStatement,
        context.startDate,
        context.endDate
      );
    }
  };

  const isLoading =
    incomeStatementStore.gettingIncomeStatement && !hasLoaded;
  const hasError = !isEmpty(incomeStatementStore.incomeStatementError);
  const hasData =
    incomeStatement?.income?.items?.length > 0 ||
    incomeStatement?.expenditure?.items?.length > 0;
  const currentStartDate = startDate
    ? dayjs(startDate).format("YYYY-MM-DD")
    : dayjs().startOf("month").format("YYYY-MM-DD");
  const currentEndDate = endDate
    ? dayjs(endDate).format("YYYY-MM-DD")
    : dayjs().format("YYYY-MM-DD");

  return (
    <div>
      <Card
        bordered={false}
        title={
          <div>
            <Title level={4} style={{ margin: 0 }}>
              PROJECT INCOME STATEMENT
            </Title>
            <div
              style={{ fontSize: "14px", color: "#666", marginTop: "4px" }}
            >
              Period: {currentStartDate} to {currentEndDate}
            </div>
          </div>
        }
        extra={
          <Space>
            <AppButton
              text="RELOAD"
              loadingText="RELOADING..."
              loading={incomeStatementStore.gettingIncomeStatement}
              iconBefore={<AiOutlineReload />}
              onClick={reloadIncomeStatement}
              disabled={!projectId}
            />
            <AppButton
              text="DOWNLOAD EXCEL"
              danger
              type="dashed"
              loadingText="DOWNLOADING..."
              iconBefore={<AiOutlineFileExcel />}
              loading={incomeStatementStore.downloadingIncomeStatement}
              onClick={downloadIncomeStatement}
              disabled={!hasData}
            />
            <AppButton
              text="DOWNLOAD PDF"
              type="primary"
              loadingText="GENERATING..."
              iconBefore={<AiOutlineFilePdf />}
              onClick={downloadIncomeStatementPdf}
              disabled={!hasData}
            />
          </Space>
        }
      >
        <Form
          form={form}
          layout="inline"
          onFinish={handleGetIncomeStatement}
          style={{ marginBottom: "16px" }}
        >
          <AppInput
            name="projectId"
            type="select"
            label="Project"
            rules={[{ required: true, message: "Please select a project" }]}
            options={map(cashbookStore.metadata?.projects, (project) => ({
              value: project.id,
              label: project.name,
            }))}
            inputAttributes={{
              onChange: (value) => setProjectId(value),
            }}
          />
          <AppInput
            name="startDate"
            label="Start Date"
            type="date"
            rules={[{ required: true, message: "Please select a start date" }]}
            inputAttributes={{
              onChange: (date) => {
                setStartDate(date);
              },
            }}
          />
          <AppInput
            name="endDate"
            label="End Date"
            type="date"
            rules={[{ required: true, message: "Please select an end date" }]}
            inputAttributes={{
              onChange: (date) => {
                setEndDate(date);
              },
              disabledDate: (current) =>
                current && startDate && current.isBefore(dayjs(startDate), "day"),
            }}
          />
          <Form.Item>
            <AppButton
              text="GET PROJECT INCOME STATEMENT"
              loadingText="Loading..."
              loading={incomeStatementStore.gettingIncomeStatement}
              htmlType="submit"
            />
          </Form.Item>
        </Form>
        {!projectId ? (
          <Alert
            type="info"
            showIcon
            message="Please select a project and period to view the income statement."
          />
        ) : isLoading ? (
          <div style={{ textAlign: "center", padding: "50px" }}>
            <Spin size="large" />
            <div style={{ marginTop: "16px" }}>
              Loading project income statement...
            </div>
          </div>
        ) : hasError ? (
          <Alert
            type="error"
            showIcon
            message="Error Loading Project Income Statement"
          />
        ) : !hasData ? (
          <Alert
            type="info"
            showIcon
            message="No Income Statement Data"
            description="No income statement data available for the selected period and project."
          />
        ) : (
          <IncomeStatementTable incomeStatementData={incomeStatement} />
        )}
      </Card>
    </div>
  );
};

export default ProjectIncomeStatement;

